/**
 * Merchant Nostr Identity & Activity Publishing
 *
 * Allows merchants to optionally publish BCH merchant presence to Nostr
 * for decentralized merchant discovery. Privacy-preserving by design.
 *
 * Kind 35341 (addressable): BCH Merchant Listing
 * Kind 7531 (regular): BCH Merchant Activity Heartbeat
 *
 * See NIP.md for full protocol documentation.
 */

import { generateSecretKey, getPublicKey } from 'nostr-tools';
import { nip19 } from 'nostr-tools';

// ─── Constants ────────────────────────────────────────────────────────────────

export const MERCHANT_LISTING_KIND = 35341;
export const MERCHANT_ACTIVITY_KIND = 7531;

const MERCHANT_NSEC_KEY = 'bch-pos:merchant-nsec';
const MERCHANT_PUBKEY_KEY = 'bch-pos:merchant-pubkey';
const LAST_HEARTBEAT_KEY = 'bch-pos:last-heartbeat';

// Minimum interval between activity heartbeats (24 hours)
const HEARTBEAT_INTERVAL_MS = 24 * 60 * 60 * 1000;

// ─── Keypair Management ──────────────────────────────────────────────────────

export interface MerchantIdentity {
  nsec: string;
  pubkey: string;
  npub: string;
}

/** Get the existing merchant identity, or null if none exists */
export function loadMerchantIdentity(): MerchantIdentity | null {
  try {
    const nsec = localStorage.getItem(MERCHANT_NSEC_KEY);
    const pubkey = localStorage.getItem(MERCHANT_PUBKEY_KEY);
    if (!nsec || !pubkey) return null;
    return { nsec, pubkey, npub: nip19.npubEncode(pubkey) };
  } catch {
    return null;
  }
}

/** Generate a new merchant identity and store it */
export function generateMerchantIdentity(): MerchantIdentity {
  const sk = generateSecretKey();
  const pubkey = getPublicKey(sk);
  const nsec = nip19.nsecEncode(sk);
  const npub = nip19.npubEncode(pubkey);
  localStorage.setItem(MERCHANT_NSEC_KEY, nsec);
  localStorage.setItem(MERCHANT_PUBKEY_KEY, pubkey);
  return { nsec, pubkey, npub };
}

/** Import an existing nsec */
export function importMerchantNsec(nsec: string): MerchantIdentity {
  const decoded = nip19.decode(nsec);
  if (decoded.type !== 'nsec') throw new Error('Not a valid nsec');
  const pubkey = getPublicKey(decoded.data);
  const npub = nip19.npubEncode(pubkey);
  localStorage.setItem(MERCHANT_NSEC_KEY, nsec);
  localStorage.setItem(MERCHANT_PUBKEY_KEY, pubkey);
  return { nsec, pubkey, npub };
}

/** Get or create the merchant identity (lazy init) */
export function ensureMerchantIdentity(): MerchantIdentity {
  const existing = loadMerchantIdentity();
  if (existing) return existing;
  return generateMerchantIdentity();
}

/** Remove the stored merchant identity and heartbeat timestamp */
export function clearMerchantIdentity(): void {
  try {
    localStorage.removeItem(MERCHANT_NSEC_KEY);
    localStorage.removeItem(MERCHANT_PUBKEY_KEY);
    localStorage.removeItem(LAST_HEARTBEAT_KEY);
  } catch { /* ignore */ }
}

// ─── Heartbeat Throttle ──────────────────────────────────────────────────────

/** Check if enough time has passed since the last heartbeat */
export function canPublishHeartbeat(): boolean {
  try {
    const last = localStorage.getItem(LAST_HEARTBEAT_KEY);
    if (!last) return true;
    return Date.now() - parseInt(last, 10) >= HEARTBEAT_INTERVAL_MS;
  } catch {
    return true;
  }
}

/** Record that a heartbeat was just published */
export function recordHeartbeat(): void {
  try {
    localStorage.setItem(LAST_HEARTBEAT_KEY, Date.now().toString());
  } catch { /* ignore */ }
}

// ─── Event Builders ──────────────────────────────────────────────────────────

export interface MerchantProfile {
  name: string;
  description?: string;
  category?: string;
  website?: string;
  geohash?: string;
}

/** Build a kind 35341 merchant listing event template (unsigned) */
export function buildMerchantListing(profile: MerchantProfile) {
  const tags: string[][] = [
    ['d', 'bch-merchant'],
    ['t', 'bch-merchant'],
    ['alt', 'BCH merchant listing'],
    ['title', profile.name],
  ];

  if (profile.description) tags.push(['summary', profile.description]);
  if (profile.category) tags.push(['t', profile.category.toLowerCase().trim()]);
  if (profile.website) tags.push(['r', profile.website]);
  if (profile.geohash) tags.push(['g', profile.geohash]);

  return {
    kind: MERCHANT_LISTING_KIND,
    content: profile.description || '',
    tags,
  };
}

/** Build a kind 7531 activity heartbeat event template (unsigned) */
export function buildActivityHeartbeat() {
  return {
    kind: MERCHANT_ACTIVITY_KIND,
    content: '',
    tags: [
      ['t', 'bch-merchant-activity'],
      ['alt', 'BCH merchant activity heartbeat'],
    ],
  };
}

/**
 * Build a kind 7531 payment heartbeat event template (unsigned).
 * Published once per completed sale — signals that BCH was actively spent
 * at this merchant. Contains no amounts, addresses, or identifying data.
 */
export function buildPaymentHeartbeat() {
  return {
    kind: MERCHANT_ACTIVITY_KIND,
    content: '',
    tags: [
      ['t', 'bch-merchant-activity'],
      ['t', 'bch-payment'],
      ['alt', 'BCH payment received at merchant'],
    ],
  };
}

// ─── Geohash Encoding ────────────────────────────────────────────────────────

const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

/**
 * Encode latitude/longitude to a geohash string.
 * @param precision Number of characters (default 5 ≈ ~5 km, city-level)
 */
export function encodeGeohash(lat: number, lng: number, precision = 5): string {
  let latMin = -90, latMax = 90;
  let lngMin = -180, lngMax = 180;
  let hash = '';
  let bit = 0;
  let ch = 0;
  let isLng = true;

  while (hash.length < precision) {
    if (isLng) {
      const mid = (lngMin + lngMax) / 2;
      if (lng >= mid) { ch = ch | (1 << (4 - bit)); lngMin = mid; }
      else { lngMax = mid; }
    } else {
      const mid = (latMin + latMax) / 2;
      if (lat >= mid) { ch = ch | (1 << (4 - bit)); latMin = mid; }
      else { latMax = mid; }
    }
    isLng = !isLng;
    bit++;
    if (bit === 5) {
      hash += BASE32[ch];
      bit = 0;
      ch = 0;
    }
  }
  return hash;
}
